/**
 * Composing a message from a sharing form.
 *
 * The garden flow writes its message from the form the manager authored. This
 * is the VISITOR over that form: the body is the form run as a program, each
 * slot's statements in turn (`evaluateForm`); completeness is a visit asking
 * each slot whether the sender has answered it; and the questions the sender
 * is asked are that visit filtered to the slots that take an answer.
 *
 * The visit is over ANSWERED slots. `answerForm` takes the authored form and
 * the sender's stored answers and hands back the same slots carrying their
 * answers. From there nothing is looked up in a map: a slot
 * knows what it was asked and what the sender said.
 *
 * Nothing here switches on a slot type. Every per-type answer comes from
 * `SLOT_LOGIC` through `SlotLogic`'s one-liners, so a new slot type declares
 * what it composes in the same one place it declares its display name, its preview and
 * its warning — and the registry's `satisfies` clause names it if it forgets.
 *
 * A text, optional or select slot can reference a variable (e.g. `{greeting}`)
 * that a different field assigned, or a primitive (e.g.
 * `{_recipientInformalName}`). A name resolves in the slot's `Environment`,
 * walking outward from the variables assigned before it to the field ids, then
 * the primitives of the message's recipients, its intent and the session.
 *
 * The form is a fact of the intent frame (`FormFacts`): an intent's messages
 * all compose from one answered form, and each message extends that
 * environment with its recipients. `evaluateForm` builds the form's own
 * frames below the recipients, where a slot naming the recipient can see them,
 * and hands back the environment each slot prints in.
 */

import { ALLOWED_DETAIL_MAX_LENGTH } from '@/modules/config/constants';
import { TemplateLogic } from '@/modules/template/logic';
import type { AddressingMode } from '@/modules/share';
import type { MessageIntent, SharingModeGate } from '@/modules/template/types';
import { SlotLogic } from './slot_logic';
import { SharingConfigLogic } from './sharing_config_logic';
import {
  EnvironmentLogic,
  type Environment,
  ExpressionLogic,
  type Expression,
  StatementLogic,
} from './lang';
import { readsReach, referencedNames, slotReads } from './schema';
import type { AllowedDetailSettings } from '@/modules/topic/types';
import type { AnsweredSlot, SharingFormSlot, SharingConfig } from './types';

/**
 * What the intent frame knows about the form its messages compose from: the
 * form carrying the sender's answers, and which sharing mode's message it is.
 * `'all'` reads the form itself rather than one message it can produce, which
 * is what validation and the builder want.
 */
export interface FormFacts {
  readonly form: readonly AnsweredSlot[];
  readonly sharingMode: SharingModeGate;
}

/** The sender's answers as they are stored, keyed by the id of the slot that answers under it. */
export type SlotAnswers = Readonly<Record<string, string>>;

/** Which of a form's slots one message is written from. */
export interface ComposeModes {
  /**
   * Whether this share is the sender's own first word about the topic or one
   * they are passing on. `'all'` reads the form itself rather than one message
   * it can produce, which is what validation and the builder want.
   */
  readonly sharingMode: SharingModeGate;
  /** How many people this message reaches. */
  readonly addressingMode: AddressingMode;
}

/**
 * The form with the sender's answers on its slots. An answer-taking slot gets
 * whatever is stored under its id; text and goal slots carry none.
 *
 * This is the one place the stored map is read. `SlotAnswers` is how answers
 * PERSIST — `useShareFormChoices` keeps them keyed by field id across the session
 * — and an answered slot is how they are COMPOSED with.
 */
function answerForm(
  form: readonly SharingFormSlot[],
  answers: SlotAnswers
): readonly AnsweredSlot[] {
  return form.map(slot => {
    return SlotLogic.withAnswer(
      slot,
      SlotLogic.isAnswerable(slot) ? (answers[slot.id] ?? '') : ''
    );
  });
}

/** The slots this sharing mode's message is written from, in form order. */
function slotsInMode<S extends SharingFormSlot>(
  form: readonly S[],
  sharingMode: SharingModeGate
): readonly S[] {
  return form.filter(slot =>
    SlotLogic.isCompatibleWithSharingMode(slot, sharingMode)
  );
}

/** Every variable the form's slots define. */
function declaredVariableNames(
  form: readonly SharingFormSlot[]
): ReadonlySet<string> {
  return new Set(
    form.flatMap(slot => {
      const name = SlotLogic.declaredVariableName(slot);
      return name ? [name] : [];
    })
  );
}

/**
 * A form evaluated for one message: the environment after its last slot, the
 * environment each slot evaluates in (see `Environment`), and the body its
 * statements emit. The environments by position share every frame but the
 * innermost.
 */
export interface EvaluatedForm<F = unknown> {
  /** The environment after the form's last slot, where its variables stand as the form leaves them. */
  readonly resultingEnvironment: Environment<F>;
  /**
   * The environment each slot in this message evaluates in, by slot id: every
   * field id, and the variables defined before it. A slot another sharing mode
   * owns is not in this message, so it has no entry.
   */
  readonly slotEnvironments: ReadonlyMap<string, Environment<F>>;
  /**
   * What the form's `emit` statements put into the message, in order, joined
   * and tidied. An emit of a field id or variable forces the same thunk a
   * lookup of that name finds, so a slot is evaluated once either way.
   */
  body(): string;
}

/**
 * The form one message composes from, run in `recipientsEnv` — the message's
 * recipient frame, under the intent frame whose `FormFacts` name the form.
 * This is SICP's eval-sequence over the statements the form's slots stand
 * for (`SlotLogic.asStatements`), in form order.
 *
 * A frame binding every variable the form defines to `''` comes first, over
 * the recipients, so a slot naming the recipient can see them. Then the
 * statements run in order. A define extends the current environment with a
 * frame binding just its name, and an emit records its expression with the
 * environment at that point, for `body` to evaluate. The environment at the
 * start of each slot's statements is the one that slot evaluates in. A name is
 * only ever a variable some slot defines explicitly (`define`); a slot's id is
 * never one.
 *
 * Every define binds a thunk: its expression evaluates in the environment at
 * its slot's position the first time it is needed, by a lookup or by an
 * emit, and once per message however often it is read (see `Thunk`). A field
 * defines its variable as what it prints rather than the raw answer, so a
 * Text slot interpolating `{greeting}` gets what the greeting actually puts in
 * the message — a primitive option resolved, a write-in verbatim — and not
 * the stored key. Slots another sharing mode owns are not run, since this
 * message never asks them and any answer they hold is left over from another
 * share; a variable only they define resolves as blank.
 *
 * The frames are built from the answers on the form each time, so filling in
 * the form, or restoring saved answers after a reload, is what assigns its
 * variables.
 */
function evaluateForm<F extends FormFacts>(
  recipientsEnv: Environment<F>
): EvaluatedForm<F> {
  const { form, sharingMode } = EnvironmentLogic.factsOf(recipientsEnv);
  const program = slotsInMode(form, sharingMode).map(slot => ({
    slot,
    statements: SlotLogic.asStatements(slot),
  }));

  const blanks = EnvironmentLogic.touchBindings(
    recipientsEnv,
    Array.from(declaredVariableNames(form), name => ({ name }))
  );

  const slotEnvironments = new Map<string, Environment<F>>();
  const emits: { expr: Expression; env: Environment }[] = [];
  let current: Environment<F> = blanks;
  for (const { slot, statements } of program) {
    slotEnvironments.set(slot.id, current);
    current = StatementLogic.run(statements, current, (expr, env) =>
      emits.push({ expr, env })
    );
  }

  return {
    resultingEnvironment: current,
    slotEnvironments,
    body: () =>
      TemplateLogic.tidyBody(
        emits.map(({ expr, env }) => ExpressionLogic.print(expr, env)).join('')
      ),
  };
}

/**
 * A form's environment outside any session or message, with no primitive
 * values bound: the intent frame for `form` over `enclosing`.
 */
function formEnvironment(
  form: readonly AnsweredSlot[],
  sharingMode: SharingModeGate = 'all',
  enclosing: Environment = EnvironmentLogic.baseEnvironment
): Environment<FormFacts> {
  return EnvironmentLogic.extendWithDefinitions(
    enclosing,
    { form, sharingMode },
    []
  );
}

/**
 * The questions this message asks the sender, in the order the message asks
 * them — form order, which is body order. A slot another sharing mode owns is
 * not in this message at all, and one with nothing worth asking about in this
 * addressing mode is not a question (see `SlotLogic.isVestigial`).
 */
function answerableSlots(
  form: readonly AnsweredSlot[],
  evaluated: EvaluatedForm,
  modes: ComposeModes
): readonly AnsweredSlot[] {
  return slotsInMode(form, modes.sharingMode).filter(
    slot =>
      SlotLogic.isAnswerable(slot) &&
      !SlotLogic.isVestigial(
        slot,
        evaluated.slotEnvironments.get(slot.id) ??
          evaluated.resultingEnvironment,
        modes.addressingMode
      )
  );
}

/**
 * The message this form sends: what its statements emit, in form order,
 * joined and tidied. A slot the author left out of the message (`dontEmit`)
 * emits nothing, though it is still asked and any variable it defines still
 * holds its answer.
 *
 * The addressing mode is deliberately not asked for. What a mode decides is
 * which answers the sender is OFFERED; an answer already on a slot composes
 * the same way either way, so a message reads the same however it is
 * addressed.
 */
function composeBody(recipientsEnv: Environment<FormFacts>): string {
  return evaluateForm(recipientsEnv).body();
}

/**
 * The names of the slots still waiting on the sender. The allowed detail's
 * slot may be left blank, so `allowedDetailSlotId` never waits.
 */
function missingAnswerNames(
  recipientsEnv: Environment<FormFacts>,
  addressingMode: AddressingMode,
  allowedDetailSlotId: string | null = null
): readonly string[] {
  const { form, sharingMode } = EnvironmentLogic.factsOf(recipientsEnv);
  const evaluated = evaluateForm(recipientsEnv);
  return answerableSlots(form, evaluated, { sharingMode, addressingMode })
    .filter(
      slot =>
        !SlotLogic.isAnswered(
          slot,
          evaluated.slotEnvironments.get(slot.id) ??
            evaluated.resultingEnvironment,
          addressingMode
        )
    )
    .map(slot => slot.id)
    .filter(name => name !== allowedDetailSlotId);
}

/** Whether every question this message asks, bar the allowed detail's, has been answered. */
function isComplete(
  recipientsEnv: Environment<FormFacts>,
  addressingMode: AddressingMode,
  allowedDetailSlotId: string | null = null
): boolean {
  return (
    missingAnswerNames(recipientsEnv, addressingMode, allowedDetailSlotId)
      .length === 0
  );
}

/**
 * Whether an answer stored under `name` is one this form's slot declares but
 * no longer offers in `addressingMode` — a greeting that names its one
 * recipient, picked while texting individually, after the sender switched to a
 * group text. The sender's own words are never stale, and neither is an answer
 * for a name no slot in the form answers under.
 */
function isStaleAnswer(
  form: readonly SharingFormSlot[],
  name: string,
  value: string,
  addressingMode: AddressingMode
): boolean {
  if (!value) return false;
  const slot = answerForm(form, {}).find(s => s.id === name);
  if (!slot) return false;
  if (SlotLogic.possibleValues(slot, addressingMode).includes(value)) {
    return false;
  }
  return SlotLogic.possibleValues(slot).includes(value);
}

/** One message a share sends: its intent, and how many people it reaches. */
export interface SentMessage {
  readonly intent: MessageIntent;
  readonly addressingMode: AddressingMode;
}

/**
 * For each soft-linked slot in a share, keyed by its id, the id of the linked
 * slot the sender is asked in its place: its own id when it is the one asked
 * (see `linkedQuestions`).
 */
export type AskedSlotIdByLinkedSlotId = ReadonlyMap<string, string>;

/**
 * For each soft-linked slot in the messages this share sends, the id of the
 * linked slot the sender is asked in its place, or its own id when it is the
 * one asked.
 *
 * Linked slots ask one question across intents (`SharingConfigLogic`), so the
 * sender answers it once: in the first of `messages` — the intents this share
 * sends, in the order the composer asks them — whose slot in the set this
 * sharing mode asks. Every later slot in the set is left off the form, and
 * `answerLinked` gives it the same answer. A message reaching a different
 * number of people is offered different answers (a greeting naming its one
 * recipient is not offered to a group), so a set is asked once per
 * addressing mode.
 */
function linkedQuestions(
  config: SharingConfig,
  messages: readonly SentMessage[],
  sharingMode: SharingModeGate
): AskedSlotIdByLinkedSlotId {
  const askedFor = new Map<string, string>();
  const questions = new Map<string, string>();
  for (const { intent, addressingMode } of messages) {
    const form = formForIntent(config, intent) ?? [];
    for (const slot of slotsInMode(form, sharingMode)) {
      const link = SlotLogic.linkIdOf(slot);
      if (link === null || !SlotLogic.isAnswerable(slot)) continue;
      const key = `${addressingMode} ${link}`;
      const asked = askedFor.get(key) ?? slot.id;
      askedFor.set(key, asked);
      questions.set(slot.id, asked);
    }
  }
  return questions;
}

/**
 * The stored answers with every slot left off the form (see
 * `linkedQuestions`) holding the answer of the slot asked in its place. An
 * answer can be stored before this share knows which slot is asked — restored
 * from an earlier visit, or given before the recipients were picked — and
 * `answerLinked` only copies answers given from then on. Returns `answers`
 * itself when every such slot already holds its asked slot's answer.
 */
function fillLinkedAnswers(
  questions: AskedSlotIdByLinkedSlotId,
  answers: SlotAnswers
): SlotAnswers {
  let next: Record<string, string> | null = null;
  for (const [id, asked] of Array.from(questions)) {
    const answer = answers[asked];
    if (asked === id || answer === undefined || answers[id] === answer) {
      continue;
    }
    next ??= { ...answers };
    next[id] = answer;
  }
  return next ?? answers;
}

/**
 * The stored answers after the sender answers the slot `slotId` names: the
 * answer stored under its id, and copied under the id of every slot linked
 * to it that the sender is not asked on its own (see
 * `linkedQuestions`). A linked slot this share leaves off the form prints
 * it, and one in a message this share does not send holds it too, so every
 * copy carries the latest answer, and keeps it if the slot is ever unlinked.
 */
function answerLinked(
  config: SharingConfig,
  questions: AskedSlotIdByLinkedSlotId,
  answers: SlotAnswers,
  slotId: string,
  answer: string
): SlotAnswers {
  const next: Record<string, string> = { ...answers, [slotId]: answer };
  for (const id of SharingConfigLogic.linkedPeers(config, slotId)) {
    const asked = questions.get(id);
    if (asked === undefined || asked === slotId) next[id] = answer;
  }
  return next;
}

/**
 * The form one intent is written from, or null when the config declares none —
 * a config carries a whole intent group, so this is null only for an intent the
 * group does not include.
 */
function formForIntent(
  config: SharingConfig,
  intent: MessageIntent
): readonly SharingFormSlot[] | null {
  return config.forms.find(form => form.intent === intent)?.form ?? null;
}

/**
 * Whether this form's reshare message tells the story of how it reached the
 * sender — some slot it prints in the reshare sharing mode names
 * `{_reshareStory}`, in its text or any option, directly or through the
 * variables it reads. A reshare composed from such a form needs
 * the sender's connection to whoever passed it on, which the composer asks
 * once for the whole session rather than as one of this form's questions.
 */
function contributesReshareStory(form: readonly SharingFormSlot[]): boolean {
  const slots = slotsInMode(form, 'reshare');
  const reads = slotReads(slots);
  const namesStory = (at: number) =>
    referencedNames(slots[at]).includes('_reshareStory');
  return slots.some(
    (slot, index) =>
      SlotLogic.emitsInMessage(slot) && readsReach(reads, index, namesStory)
  );
}

/**
 * The allowed detail a sender's leg carries: their answer to the Settings
 * tab's slot, as `resolve` gives what the slot `slotId` prints (`mirror`). The
 * tab's prefix and suffix stay on the topic for the story renderer, which
 * frames the answer as a sentence ("I would bring a mirror to the island").
 *
 * Empty when the topic names no slot for the detail, or when the slot is
 * unanswered, so no blank detail travels. An answer too long for the leg is
 * shortened with an ellipsis rather than failing the send.
 */
function allowedDetail(
  settings: AllowedDetailSettings | null | undefined,
  resolve: (name: string) => string | null
): string {
  if (!settings?.slotId) return '';
  const answer = Array.from((resolve(settings.slotId) ?? '').trim());
  if (answer.length <= ALLOWED_DETAIL_MAX_LENGTH) return answer.join('');
  return `${answer
    .slice(0, ALLOWED_DETAIL_MAX_LENGTH - 1)
    .join('')
    .trimEnd()}…`;
}

/** The visitor that composes a message from a sharing form's slots. */
export const ComposeLogic = {
  answerForm,
  formEnvironment,
  evaluateForm,
  answerableSlots,
  composeBody,
  missingAnswerNames,
  isComplete,
  isStaleAnswer,
  formForIntent,
  linkedQuestions,
  answerLinked,
  fillLinkedAnswers,
  contributesReshareStory,
  allowedDetail,
};
